import * as React from "react";
import styled from "@emotion/styled";
import {
  Box,
  BoxProps,
  Tooltip,
  TooltipProps,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router";

const Card = styled(Box)`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  padding: 1.5rem;
  border-radius: 1rem;
  color: ${(p) => p.theme.text.colors.primary};
  background-color: ${(p) => p.theme.colors.background};
  border: 1px solid ${(p) => p.theme.colors.border2};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  width: 100%;
  min-height: 164px;

  img {
    height: 38px;
    width: auto;
  }

  &:hover {
    border-color: ${(p) => p.theme.colors.border};
  }

  &[aria-disabled="true"] {
    opacity: 0.4;
    pointer-events: none;
  }
`;

const CardTitle = styled(Typography)`
  color: ${(p) => p.theme.text.colors.primary};
  font-family: ${(p) => p.theme.title.fonts.primary};
  font-size: ${(p) => p.theme.text.fontSizes.l};
  margin: 0.75rem 0 0 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  flex: 1;
`;

interface Props extends BoxProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  navigateOptions: {
    path: string;
    options?: {
      newTab?: boolean;
    };
  };
  disabled?: boolean;
  tooltip?: {
    title: TooltipProps["title"];
    placement?: TooltipProps["placement"];
    arrow?: TooltipProps["arrow"];
  };
}

export const DashboardCard = (props: Props) => {
  const {
    icon,
    navigateOptions,
    title,
    description,
    disabled,
    tooltip,
    ...rest
  } = props;

  const navigate = useNavigate();

  const handleClick = (
    path: string,
    options?: {
      newTab?: boolean;
    }
  ) => {
    const { newTab = true } = options || {};
    const isExternalLink = path.startsWith("http");

    if (isExternalLink) {
      if (newTab) {
        window.open(path, "_blank");
        return;
      }
      window.location.href = path;
      return;
    }

    navigate(path);
  };

  const content = (
    <Card
      key={rest.key ?? navigateOptions.path}
      {...rest}
      onClick={() => handleClick(navigateOptions.path, navigateOptions.options)}
      aria-disabled={disabled}
    >
      {icon}
      <CardContent>
        <CardTitle variant="h6">{title}</CardTitle>
        <Typography variant="body2">{description}</Typography>
      </CardContent>
    </Card>
  );

  const card = tooltip?.title ? (
    <Tooltip
      title={tooltip.title}
      arrow={tooltip.arrow ?? true}
      placement={tooltip.placement ?? "top"}
    >
      <div>{content}</div>
    </Tooltip>
  ) : (
    content
  );

  return card;
};
